// ============================================
// Auth - Login Module (ES6)
// 로그인 모달 및 인증
// ============================================

import { FUNCTIONS_BASE } from '../config.js';
import { showToast, showGlobalLoading, hideGlobalLoading } from '../utils.js';
import { getAuth, getCurrentUser, setUserNickname } from './state.js';
import { closeChatOverlay } from '../chat/overlay.js';
import { useAppStore } from '../../stores/appStore.js';
import { useHomeStore } from '../../stores/homeStore.js';
import { useCreatorStore } from '../../stores/creatorStore.js';
import { useMypageStore } from '../../stores/mypageStore.js';
import { useProfileStore } from '../../stores/profileStore.js';
import { closeSettingsModal } from '../settings/chat-modal.js';

// ============================================
// Login Modal
// ============================================

export function showLoginModal() {
  const modal = document.getElementById('loginModal');
  modal.style.display = 'flex';
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      modal.classList.add('active');
    });
  });
}

export function closeLoginModal() {
  const modal = document.getElementById('loginModal');
  modal.classList.remove('active');
  setTimeout(() => {
    modal.style.display = 'none';
  }, 250);
}

export function requireLogin(callback) {
  if (getCurrentUser()) {
    callback();
  } else {
    showLoginModal();
  }
}

// ============================================
// Authentication Mode
// ============================================

export function showSignupMode() {
  // 닉네임 필드 표시
  const emailNicknameGroup = document.getElementById('emailNicknameGroup');
  if (emailNicknameGroup) emailNicknameGroup.style.display = 'block';

  // 약관 동의 표시
  const authTermsGroup = document.getElementById('authTermsGroup');
  if (authTermsGroup) authTermsGroup.style.display = 'block';

  // 비밀번호 찾기 숨기기
  const forgotPasswordBtn = document.getElementById('forgotPasswordBtn');
  if (forgotPasswordBtn) forgotPasswordBtn.style.display = 'none';

  // 타이틀 변경
  const emailLoginTitle = document.getElementById('emailLoginTitle');
  if (emailLoginTitle) emailLoginTitle.textContent = '회원가입';

  // 버튼 텍스트 변경
  const authSubmitBtn = document.getElementById('authSubmitBtn');
  if (authSubmitBtn) authSubmitBtn.textContent = '회원가입';

  const authModeToggleBtn = document.getElementById('authModeToggleBtn');
  if (authModeToggleBtn) {
    authModeToggleBtn.innerHTML = '이미 계정이 있으신가요? <span>로그인</span>';
  }
}

export function showLoginMode() {
  // 닉네임 필드 숨기기
  const emailNicknameGroup = document.getElementById('emailNicknameGroup');
  if (emailNicknameGroup) emailNicknameGroup.style.display = 'none';

  // 약관 동의 숨기기
  const authTermsGroup = document.getElementById('authTermsGroup');
  if (authTermsGroup) authTermsGroup.style.display = 'none';

  // 비밀번호 찾기 표시
  const forgotPasswordBtn = document.getElementById('forgotPasswordBtn');
  if (forgotPasswordBtn) forgotPasswordBtn.style.display = 'block';

  // 타이틀 변경
  const emailLoginTitle = document.getElementById('emailLoginTitle');
  if (emailLoginTitle) emailLoginTitle.textContent = '로그인';

  // 버튼 텍스트 변경
  const authSubmitBtn = document.getElementById('authSubmitBtn');
  if (authSubmitBtn) authSubmitBtn.textContent = '로그인';

  const authModeToggleBtn = document.getElementById('authModeToggleBtn');
  if (authModeToggleBtn) {
    authModeToggleBtn.innerHTML = '계정이 없으신가요? <span>회원가입</span>';
  }
}

// ============================================
// Firebase Error Message Mapping
// ============================================

function getAuthErrorMessage(error) {
  const errorMap = {
    'auth/user-not-found': '등록되지 않은 이메일입니다',
    'auth/wrong-password': '비밀번호가 올바르지 않습니다',
    'auth/invalid-email': '올바른 이메일 형식이 아닙니다',
    'auth/email-already-in-use': '이미 사용 중인 이메일입니다',
    'auth/weak-password': '비밀번호는 6자 이상이어야 합니다',
    'auth/too-many-requests': '로그인 시도가 너무 많습니다. 잠시 후 다시 시도해주세요',
    'auth/invalid-credential': '이메일 또는 비밀번호를 확인해주세요',
    'auth/network-request-failed': '네트워크 연결을 확인해주세요',
    'auth/user-disabled': '비활성화된 계정입니다',
    'auth/operation-not-allowed': '이메일/비밀번호 로그인이 비활성화되어 있습니다'
  };
  return errorMap[error.code] || '로그인에 실패했습니다. 다시 시도해주세요';
}

// ============================================
// Email Authentication
// ============================================

export async function handleAuth(mode) {
  const email = document.getElementById('authEmail').value?.trim();
  const password = document.getElementById('authPassword').value;
  const nickname = document.getElementById('authNickname')?.value?.trim() || '';

  if (!email || !password) {
    showToast('이메일과 비밀번호를 입력하세요', true);
    return;
  }

  if (mode === 'signup') {
    if (!nickname || nickname.length < 2) {
      showToast('닉네임을 2자 이상 입력하세요', true);
      return;
    }
    if (nickname.length > 12) {
      showToast('닉네임은 12자 이하로 입력하세요', true);
      return;
    }

    // 약관 동의 체크
    const agreeTerms = document.getElementById('agreeTerms')?.checked;
    const agreePrivacy = document.getElementById('agreePrivacy')?.checked;
    if (!agreeTerms || !agreePrivacy) {
      showToast('필수 약관에 동의해주세요', true);
      return;
    }
  }

  showGlobalLoading();
  const auth = getAuth();

  try {
    if (mode === 'login') {
      const userCredential = await auth.signInWithEmailAndPassword(email, password);

      // 이메일 인증 확인
      if (!userCredential.user.emailVerified) {
        hideGlobalLoading();
        showEmailVerificationModal(email);
        return;
      }
    } else {
      const userCredential = await auth.createUserWithEmailAndPassword(email, password);

      // 이메일 인증 발송
      await userCredential.user.sendEmailVerification();

      const idToken = await userCredential.user.getIdToken();
      await fetch(`${FUNCTIONS_BASE}/createUser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ nickname })
      });

      setUserNickname(nickname);
      hideGlobalLoading();

      // 이메일 인증 안내 모달 표시
      closeEmailLoginModal();
      showEmailVerificationModal(email);
      return;
    }
    // 로그인 성공 토스트 제거 (UX 간소화)
  } catch (e) {
    hideGlobalLoading();
    showToast(getAuthErrorMessage(e), true);
  }
}

// ============================================
// Google Login
// ============================================

export async function handleGoogleLogin() {
  console.log('handleGoogleLogin called');

  // Unity WebView 환경 감지 - UniWebView로 Google 로그인 요청
  // Unity에서 페이지 로드 후 window.isUniWebView = true 주입
  if (window.isUniWebView) {
    console.log('[Unity] UniWebView 환경 감지 - 네이티브 Google 로그인 요청');
    location.href = 'uniwebview://requestGoogleLogin';
    return;
  }

  const auth = getAuth();
  if (!auth) {
    console.error('Firebase auth not initialized');
    showToast('Firebase가 아직 초기화되지 않았습니다. 잠시 후 다시 시도해주세요.', 'error');
    return;
  }
  showGlobalLoading();
  try {
    // Capacitor 네이티브 환경에서는 Social Login 플러그인 사용
    const isNative = window.Capacitor?.isNativePlatform?.();
    if (isNative) {
      console.log('Capacitor 환경 감지 - SocialLogin 플러그인 사용');
      try {
        const { SocialLogin } = await import('@capgo/capacitor-social-login');

        // Google 프로바이더 초기화 (Web Client ID 사용)
        await SocialLogin.initialize({
          google: {
            webClientId: '391890616913-73t1ucrcqk8l0vq8s4enjqa7d6ra5j20.apps.googleusercontent.com'
          }
        });

        // 네이티브 Google 로그인 (Credential Manager는 기본 scope만 지원)
        const response = await SocialLogin.login({
          provider: 'google',
          options: {}
        });

        console.log('[SocialLogin] 로그인 응답:', response);

        if (!response.result?.idToken) {
          throw new Error('idToken을 받지 못했습니다');
        }

        // Firebase Credential 생성
        const credential = firebase.auth.GoogleAuthProvider.credential(
          response.result.idToken
        );

        // Firebase로 인증
        const result = await auth.signInWithCredential(credential);

        // 신규 사용자 처리
        if (result.additionalUserInfo?.isNewUser) {
          try {
            const idToken = await result.user.getIdToken();
            await fetch(`${FUNCTIONS_BASE}/createUser`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
              },
              body: JSON.stringify({ nickname: '' })
            });
          } catch (createError) {
            console.warn('createUser failed:', createError);
          }
        }
        return;
      } catch (googleError) {
        hideGlobalLoading();
        // 사용자 취소
        if (googleError.message?.includes('cancel') || googleError.message?.includes('Cancel') ||
            googleError.message?.includes('cancelled')) {
          return;
        }
        console.error('[SocialLogin] 네이티브 로그인 실패:', googleError);
        showToast('Google 로그인 실패: ' + googleError.message, true);
        return;
      }
    }

    // 웹 환경에서는 popup 방식 사용
    const provider = new firebase.auth.GoogleAuthProvider();
    const result = await auth.signInWithPopup(provider);

    if (result.additionalUserInfo?.isNewUser) {
      try {
        const idToken = await result.user.getIdToken();
        await fetch(`${FUNCTIONS_BASE}/createUser`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({ nickname: '' })
        });
      } catch (createError) {
        console.warn('createUser failed, will fallback in loadUserData:', createError);
      }
    }

    // 로그인 성공 토스트 제거 (UX 간소화)
  } catch (e) {
    hideGlobalLoading();
    // 팝업 취소 시 에러 표시 안함
    if (e.code === 'auth/popup-closed-by-user' || e.code === 'auth/cancelled-popup-request') {
      return;
    }
    showToast('Google 로그인 실패: ' + e.message, true);
  }
}

// ============================================
// Email Verification Modal
// ============================================

export function showEmailVerificationModal(email) {
  const modal = document.getElementById('emailVerificationModal');
  if (!modal) return;

  const emailText = document.getElementById('verificationEmailText');
  if (emailText) {
    emailText.textContent = `${email}으로 인증 메일을 보냈습니다`;
  }

  modal.classList.add('open');

  if (window.ModalManager) {
    window.ModalManager.register('emailVerificationModal', closeEmailVerificationModal, 'fullscreen');
    window.ModalManager.push('emailVerificationModal');
  }
}

export function closeEmailVerificationModal() {
  const modal = document.getElementById('emailVerificationModal');
  if (!modal) return;

  modal.classList.remove('open');

  if (window.ModalManager) {
    window.ModalManager.remove('emailVerificationModal', true);
  }
}

export async function resendVerificationEmail() {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    try {
      await user.sendEmailVerification();
      showToast('인증 메일을 다시 보냈습니다');
    } catch (e) {
      showToast('메일 발송에 실패했습니다. 잠시 후 다시 시도해주세요.', true);
    }
  } else {
    showToast('로그인 정보가 없습니다. 다시 로그인해주세요.', true);
  }
}

export async function checkEmailVerification() {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    await user.reload();
    if (user.emailVerified) {
      closeEmailVerificationModal();
      showToast('이메일 인증이 완료되었습니다');
      // 앱 화면으로 이동
      window.location.reload();
    } else {
      showToast('아직 이메일 인증이 완료되지 않았습니다', true);
    }
  } else {
    showToast('로그인 정보가 없습니다. 다시 로그인해주세요.', true);
  }
}

// ============================================
// Password Reset Modal
// ============================================

export function showResetPasswordModal() {
  closeEmailLoginModal();

  const modal = document.getElementById('resetPasswordModal');
  if (!modal) return;

  modal.classList.add('open');

  if (window.ModalManager) {
    window.ModalManager.register('resetPasswordModal', closeResetPasswordModal, 'fullscreen');
    window.ModalManager.push('resetPasswordModal');
  }
}

export function closeResetPasswordModal() {
  const modal = document.getElementById('resetPasswordModal');
  if (!modal) return;

  modal.classList.remove('open');

  if (window.ModalManager) {
    window.ModalManager.remove('resetPasswordModal', true);
  }
}

export async function sendPasswordResetEmail() {
  const email = document.getElementById('resetEmail').value?.trim();

  if (!email) {
    showToast('이메일을 입력하세요', true);
    return;
  }

  showGlobalLoading();

  try {
    const auth = getAuth();
    await auth.sendPasswordResetEmail(email);
    hideGlobalLoading();
    showToast('비밀번호 재설정 메일을 보냈습니다');
    closeResetPasswordModal();
  } catch (e) {
    hideGlobalLoading();
    showToast(getAuthErrorMessage(e), true);
  }
}

// ============================================
// Logout
// ============================================

export function handleLogout() {
  window._isIntentionalLogout = true;

  // 1. 모든 모달 닫기
  if (window.ModalManager) {
    window.ModalManager.closeAll();
  }

  // 2. 채팅 오버레이 닫기
  closeChatOverlay();

  // 3. 설정창 닫기
  closeSettingsModal();

  // 4. 스토어 캐시 초기화
  try {
    useAppStore.getState().resetChatState?.();
    useHomeStore.getState().reset?.();
    useCreatorStore.getState().resetEditorData?.();
    useMypageStore.getState().resetAvatar?.();
    useProfileStore.getState().reset?.();
  } catch (e) {
    console.error('[Logout] 스토어 초기화 오류:', e);
  }

  // 5. 전역 캐시 초기화
  window._chatListCache = null;
  window._currentChatMessages = null;

  // 6. DOM 화면 초기화
  // 홈 상세 페이지 닫기
  const homeDetail = document.getElementById('homeDetail');
  if (homeDetail) {
    homeDetail.classList.remove('active');
  }

  // 채팅 목록 초기화
  const chatListContainer = document.querySelector('.chat-list');
  if (chatListContainer) {
    chatListContainer.innerHTML = '';
  }

  // 마이페이지 작품 목록 초기화
  const myWorksGrid = document.querySelector('.my-works-grid');
  if (myWorksGrid) {
    myWorksGrid.innerHTML = '';
  }

  // 유저페이지 닫기
  const userPageOverlay = document.getElementById('userPageOverlay');
  if (userPageOverlay) {
    userPageOverlay.classList.remove('active');
  }

  // 프로필 관리 모달 닫기
  const profileManagerModal = document.getElementById('profileManagerModal');
  if (profileManagerModal) {
    profileManagerModal.classList.remove('active');
  }

  // 7. Firebase 로그아웃
  const auth = getAuth();
  auth.signOut();

  // 8. 홈 탭으로 이동
  if (typeof switchTab === 'function') {
    switchTab('home');
  } else if (window.switchTab) {
    window.switchTab('home');
  }

  showToast('로그아웃되었습니다');
}

// ============================================
// Email Login Modal (Bottom Sheet)
// ============================================

export function showEmailLoginModal() {
  const modal = document.getElementById('emailLoginModal');
  if (!modal) return;

  modal.classList.add('open');
  showLoginMode();

  if (window.ModalManager) {
    window.ModalManager.register('emailLoginModal', closeEmailLoginModal, 'fullscreen');
    window.ModalManager.push('emailLoginModal');
  }
}

export function closeEmailLoginModal() {
  const modal = document.getElementById('emailLoginModal');
  if (!modal) return;

  modal.classList.remove('open');

  if (window.ModalManager) {
    window.ModalManager.remove('emailLoginModal', true);
  }
}

// ============================================
// Unity WebView Google Login
// ============================================

/**
 * Unity WebView에서 Google idToken으로 로그인
 * Unity에서 호출: window.loginWithGoogleToken(idToken)
 * @param {string} idToken - Google Sign-In SDK에서 받은 idToken
 * @returns {Promise<firebase.User>} Firebase 사용자 객체
 */
export async function loginWithGoogleToken(idToken) {
  if (!idToken) {
    throw new Error('idToken이 필요합니다');
  }

  const auth = getAuth();
  if (!auth) {
    throw new Error('Firebase가 초기화되지 않았습니다');
  }

  showGlobalLoading();

  try {
    // Google Credential 생성 및 Firebase 인증
    const credential = firebase.auth.GoogleAuthProvider.credential(idToken);
    const result = await auth.signInWithCredential(credential);

    // 신규 사용자 처리
    if (result.additionalUserInfo?.isNewUser) {
      try {
        const firebaseIdToken = await result.user.getIdToken();
        await fetch(`${FUNCTIONS_BASE}/createUser`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${firebaseIdToken}`
          },
          body: JSON.stringify({ nickname: '' })
        });
      } catch (createError) {
        console.warn('[Unity] createUser failed:', createError);
      }
    }

    console.log('[Unity] Google 로그인 성공:', result.user.uid);
    return result.user;
  } catch (e) {
    console.error('[Unity] Google 로그인 실패:', e);
    throw e;
  } finally {
    hideGlobalLoading();
  }
}

// Unity → WebView 통신용 window 노출
window.loginWithGoogleToken = loginWithGoogleToken;
